import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet'
import { useCreateCategory } from '@/lib/hooks'
import { validateAndToast, createCategorySchema } from '@/lib/schemas'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  parentId: string | null
  userId?: string
  allowPersonal?: boolean
}

function toUserMessage(error: unknown): string {
  const msg = error instanceof Error ? error.message : ''
  if (msg.includes('uq_category_name_per_parent')) {
    return '同じ名前のカテゴリがすでに存在します。'
  }
  return 'カテゴリの追加に失敗しました。'
}

export function AddCategorySheet({ open, onOpenChange, parentId, userId, allowPersonal }: Props) {
  const [name, setName] = useState('')
  const [isPersonal, setIsPersonal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const createCat = useCreateCategory()

  const handleSubmit = () => {
    const payload = validateAndToast(createCategorySchema, {
      name,
      parentId: parentId || '',
      ownerId: isPersonal ? userId : null,
    })
    if (!payload) return
    setError(null)
    createCat.mutate(payload, {
      onSuccess: () => onOpenChange(false),
      onError: (err) => setError(toUserMessage(err)),
    })
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) { setName(''); setIsPersonal(false); setError(null) }
        onOpenChange(v)
      }}
    >
      <SheetContent
        side="bottom"
        className="max-h-[85vh] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>カテゴリを追加</SheetTitle>
          <SheetDescription>
            新しいカテゴリの名前を入力してください。
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 px-4">
          <div className="space-y-2">
            <Label htmlFor="add-name">カテゴリ名</Label>
            <Input
              id="add-name"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null) }}
              placeholder="例: コンビニ"
              enterKeyHint="done"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) handleSubmit()
              }}
            />
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>
          {allowPersonal && (
            <div className="space-y-2">
              <Label>公開範囲</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={!isPersonal ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setIsPersonal(false)}
                >
                  共有
                </Button>
                <Button
                  type="button"
                  variant={isPersonal ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setIsPersonal(true)}
                >
                  個人
                </Button>
              </div>
              {isPersonal && (
                <p className="text-xs text-muted-foreground">
                  個人カテゴリは自分だけに表示されます。
                </p>
              )}
            </div>
          )}
        </div>
        <SheetFooter>
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!name.trim() || createCat.isPending}
          >
            {createCat.isPending ? '追加中...' : '追加'}
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            キャンセル
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
